import { Box, Stack, Typography } from '@mui/material';
import type SvgIcon from '@mui/material/SvgIcon';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import type { IconTintName } from '../../theme/types';

export interface StatTileProps {
  /**
   * Extra content below the value, bottom-aligned within the tile. Note that the bottom alignment only
   * holds for an unlinked tile: with `to` set, the tile is wrapped in a block-level link, so the grid
   * cell's stretched height stops at the wrapper and the caption's `mt: 'auto'` becomes inert. Combining
   * `caption` with `to` also means putting interactive content inside a link, which is invalid if the
   * caption itself contains a link or a button.
   */
  readonly caption?: ReactNode;
  readonly icon: typeof SvgIcon;
  readonly label: string;
  readonly tint: IconTintName;
  readonly to?: string;
  readonly value: ReactNode;
}

export function StatTile({ caption, icon: Icon, label, tint, to, value }: StatTileProps) {
  const body = (
    <Stack spacing={1.25} sx={{ minWidth: 0, p: { xs: 2, md: 2.5 } }}>
      <Box
        aria-hidden="true"
        sx={{
          alignItems: 'center',
          bgcolor: `iconTint.${tint}.background`,
          borderRadius: 2,
          color: `iconTint.${tint}.foreground`,
          display: 'flex',
          height: 40,
          justifyContent: 'center',
          width: 40,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
      {caption === undefined ? null : <Box sx={{ mt: 'auto' }}>{caption}</Box>}
    </Stack>
  );

  if (to === undefined) return body;

  return (
    <Box
      component={Link}
      sx={{
        color: 'inherit',
        display: 'block',
        minWidth: 0,
        textDecoration: 'none',
        '&:hover': { bgcolor: 'surface.subtle' },
      }}
      to={to}
    >
      {body}
    </Box>
  );
}
